/**
 * Twin-stroke line data for a storage device whose energy can leave by two
 * different routes.
 *
 * An EV is in exactly one state per interval: charging, discharging through the
 * plug, or away and driving. The sim confirms it - plug flow and driving energy
 * are never both non-zero in the same interval - so one continuous line per
 * device can carry all three states, and only the stroke has to change. Driving
 * keeps the positive sign it had before the two-exit split, when it was still
 * folded into Discharge.
 *
 * No chart library ever appears in here. The caller turns the two returned
 * arrays into whatever a solid and a dashed line are in its own chart, which is
 * why both the Storage page (StorageDashboard/StorageChart.jsx) and the Summary
 * page (Summary/OverlayChart.jsx) can share one implementation of the join
 * selection below - the part that is genuinely hard to get right.
 *
 * A device with no driving at all - a battery, a legacy result predating the
 * driving column, or simply a vehicle that stayed put - reports
 * `hasDriving: false`, and the caller draws the solid stroke alone.
 *
 * The invariant that makes the split readable: every point on a stroke is a
 * true reading of that stroke's own quantity. The solid stroke is never above
 * zero during an away trip, and the dashed stroke is never non-zero while the
 * vehicle is plugged in. Nothing is ever borrowed from the other half.
 *
 * A second invariant follows from the first once the charts draw these as step
 * lines: neither stroke draws over an interval belonging to the other. A sample
 * under `step: "end"` owns the hour to its right, so each stroke covers exactly
 * the hours in its own state and stops. Nothing is ever written into a stroke
 * at an index belonging to the other one.
 *
 * The two halves are still joined into one readable line, but by a separate
 * `connectors` geometry rather than by samples: a zero-width vertical at each
 * hand-over boundary, which owns no interval and so cannot trespass on one. The
 * caller draws it as its own stroke. See the join note in buildTwinStrokeData
 * for why nothing in the sample array can do this job.
 */

/* Roles carried on individual data points so a tooltip can tell what it is
 * looking at. A point with no role is an ordinary plug-flow reading. */
export const DRIVING_ROLE = "driving";
/* Carried on the connector points that bridge the two strokes at a hand-over.
 * A connector is a rendering artifact - a zero-width vertical at the boundary -
 * and never a measurement, so tooltips must keep suppressing it. See
 * readTwinStrokePoint, and the join note in buildTwinStrokeData. */
export const BRIDGE_ROLE = "bridge";

/* Matches the `Number(x) || 0` the chart columns are read with, so a blank,
 * absent or unparseable cell reads as 0 rather than poisoning the arithmetic. */
const toNumber = (value) => Number(value) || 0;

/**
 * Build the solid and dashed halves of one device's line.
 *
 * @param {Array<number|string>} plugFlow  Per-interval plug charge/discharge,
 *   signed the way the charts already sign it: negative charging, positive
 *   discharging.
 * @param {Array<number|string>} [driving] Per-interval energy that left through
 *   the wheels rather than the plug, always a positive magnitude. Absent,
 *   shorter, or all-zero for a device that has no wheels exit.
 * @returns {{values: number[], solid: Array<?{value: number, role?: string}>,
 *   dashed: Array<?{value: number, role?: string}>,
 *   connectors: {solid: Array<?object>, dashed: Array<?object>},
 *   hasDriving: boolean}}
 *   `values` is the per-interval total - plug flow plus driving - and is what an
 *   aggregate line must be summed from, since the strokes carry no join samples
 *   and the connectors are not readings. `solid` and `dashed` are the same
 *   length, each null where the other one is drawn.
 *
 *   `connectors` are NOT index-aligned and are not readings: each is a flat list
 *   of two-point subpaths in explicit [categoryIndex, value] form, separated by
 *   separators, drawing the zero-width vertical that joins the two strokes at
 *   each hand-over. Draw them as their own series, with the device's colour and
 *   the matching dash, and WITHOUT `step` - stepping them would give them width
 *   and defeat the point. Their points carry BRIDGE_ROLE so a tooltip drops
 *   them, and every third point is a separator with a null y that ends one
 *   vertical before the next begins.
 */
/**
 * The per-interval total - plug flow plus driving - on its own, without the
 * strokes.
 *
 * This is the array an aggregate line must be summed from, and it is the half
 * of buildTwinStrokeData a caller needs for EVERY device whether or not that
 * device is drawn. Summary/OverlayChart.jsx builds the Storage Summary from
 * every device's values while drawing only the devices the user has switched
 * on, so it calls this eagerly for all of them and buildTwinStrokeData only for
 * the ones on screen.
 *
 * Exported so there is ONE definition of what a device's value is, rather than
 * a copy in the aggregate path that could drift from the one the strokes are
 * built on. buildTwinStrokeData calls it too.
 *
 * @param {Array<number|string>} plugFlow  Per-interval plug charge/discharge.
 * @param {Array<number|string>} [driving] Per-interval energy out through the
 *   wheels. Absent, shorter or all-zero for a device with no wheels exit.
 * @returns {number[]}
 */
export function twinStrokeValues(plugFlow, driving) {
  const plug = Array.isArray(plugFlow) ? plugFlow : [];
  const wheels = Array.isArray(driving) ? driving : [];
  return plug.map((flow, i) => toNumber(flow) + toNumber(wheels[i]));
}

export function buildTwinStrokeData(plugFlow, driving) {
  const plug = Array.isArray(plugFlow) ? plugFlow : [];
  const wheels = Array.isArray(driving) ? driving : [];

  const values = twinStrokeValues(plug, wheels);
  const isDriving = plug.map((_, i) => toNumber(wheels[i]) !== 0);

  const solid = values.map((value, i) => (isDriving[i] ? null : { value }));
  const dashed = values.map((value, i) =>
    isDriving[i] ? { value, role: DRIVING_ROLE } : null,
  );

  /* ── Joining the two halves ─────────────────────────────────
   *
   * Two things have to be true at a hand-over, and under `step: "end"` alone
   * they cannot both be:
   *
   *   1. NO GAP. The eye follows one line from plug flow, through zero, up to
   *      driving. A stroke that begins at its full value with nothing beneath
   *      it reads as broken.
   *   2. NO TRESPASS. Neither stroke paints ink over an hour belonging to the
   *      other. A device-coloured 2px line lying along y=0 is not hidden by the
   *      axis - it reads as a second line.
   *
   * WHY THEY CONFLICT, which is worth stating because both were reported as
   * bugs and the obvious fix for each causes the other. Under `step: "end"` a
   * sample owns the interval to its RIGHT, and it paints that interval whenever
   * a next column exists - even when the next sample is null (measured). A
   * vertical at boundary b is drawn only between two non-null samples of the
   * SAME stroke at b-1 and b. At a departure between plug hour t and driving
   * hour t+1, b = t+1: for the solid stroke to reach zero at b it needs a
   * sample at index t+1, a DRIVING hour, which then paints [t+1, t+2]; for the
   * dashed stroke to rise from zero at b it needs a sample at index t, a PLUG
   * hour, which then paints [t, t+1]. Either way one stroke paints an hour
   * belonging to the other. There is no third choice: joining through the data
   * always costs an interval.
   *
   * THE WAY OUT. The connector is a rendering artifact, not a reading, so it
   * does not have to live in the sample array at all. ECharts takes explicit
   * [categoryIndex, value] pairs on a category axis, so a two-point subpath at
   * a single index draws a vertical of ZERO width - it owns no interval and can
   * trespass on nothing. `connectors` below is exactly that: for every state
   * change, the piece of vertical each stroke is missing, at the boundary the
   * change happens on.
   *
   * They are DERIVED here, in the same pass and from the same arrays as the
   * strokes, rather than drawn over the top afterwards, so they cannot fall out
   * of step with the data they join.
   *
   * The alternative that was measured and rejected: emitting the whole
   * staircase explicitly as [x, y] pairs and dropping `step`. The ink is
   * correct, but an axis tooltip resolves such a series by data index rather
   * than by category, so hovering hour 2 reported hour 1 values under an "h1"
   * label. Silently wrong numbers are worse than either rendering complaint.
   *
   * Splitting the vertical at zero is what keeps each half on its own side of
   * the axis: the solid piece runs between the plug reading and zero, the
   * dashed piece between zero and the driving reading, and they meet there.
   *
   * A join in the sample array is no longer needed anywhere, including at the
   * end of the period. The last sample owns no interval and used to be drawn
   * only by a vertical arriving from a zero written into the neighbouring
   * stroke; when the period ends on a state change the connector at that same
   * boundary now draws it instead, and costs nothing. */
  const connectorSolid = [];
  const connectorDashed = [];
  /* Each connector is two points and a separator that ends the subpath.
   *
   * The separator carries the boundary's x and a null y, rather than being a
   * bare `null`. That is load-bearing, and it is not obvious: the charts' zoom
   * runs with ECharts' default `filterMode: "filter"`, which drops data items
   * whose x falls outside the zoom window - and a bare `null` has no x to
   * judge, so it is dropped ALWAYS, at every zoom level. With the separators
   * gone the remaining points join up, and this series stops being a set of
   * verticals and becomes one polyline zig-zagging between zero and each
   * driving plateau - ink at the plateau's own height, sloping across hours
   * that belong to the strokes. Giving the separator an x keeps it in the
   * window with the pair it terminates, so the break survives the zoom. */
  const addConnector = (into, boundary, from, to) => {
    if (from === to) return;
    into.push(
      { value: [boundary, from], role: BRIDGE_ROLE },
      { value: [boundary, to], role: BRIDGE_ROLE },
      { value: [boundary, null], role: BRIDGE_ROLE },
    );
  };

  for (let t = 0; t < values.length - 1; t++) {
    if (isDriving[t] === isDriving[t + 1]) continue;
    /* The change happens between t and t+1, so it belongs on the boundary
     * between them - column t+1, the instant the vehicle unplugs or plugs back
     * in. Both strokes' ink already reaches that x from their own sides. */
    const boundary = t + 1;
    const plugIndex = isDriving[t] ? t + 1 : t;
    const drivingIndex = isDriving[t] ? t : t + 1;
    addConnector(connectorSolid, boundary, values[plugIndex], 0);
    addConnector(connectorDashed, boundary, 0, values[drivingIndex]);
  }

  return {
    values,
    solid,
    dashed,
    connectors: { solid: connectorSolid, dashed: connectorDashed },
    hasDriving: isDriving.some(Boolean),
  };
}

/**
 * Interpret one datum from a twin stroke for a tooltip.
 *
 * Returns null when there is nothing to report: a gap, where the other stroke
 * holds the reading, or a join, which is a rendering device rather than a
 * measurement and would otherwise be printed twice at every transition.
 *
 * `datum` is whatever the chart handed back for the point - a plain number for
 * an ordinary series, or one of the tagged objects this module produces.
 * `fallbackValue` is used when the datum is not one of those objects.
 *
 * @returns {?{value: number, role?: string}}
 */
export function readTwinStrokePoint(datum, fallbackValue) {
  const point =
    datum && typeof datum === "object" && !Array.isArray(datum)
      ? datum
      : { value: fallbackValue };
  if (point.value === null || point.value === undefined) return null;
  if (point.role === BRIDGE_ROLE) return null;
  return point;
}

/** Whether a point read back by `readTwinStrokePoint` is driving energy. */
export const isDrivingPoint = (point) => point?.role === DRIVING_ROLE;

/**
 * Read one twin-stroke value back as a plain number, for the axis-extent and
 * min/max passes that treat a series' data as bare numbers. A gap reads as
 * null so the caller can skip it rather than fold a 0 into the range.
 *
 * @returns {?number}
 */
export function twinStrokeValue(datum) {
  if (datum === null || datum === undefined) return null;
  const raw = typeof datum === "object" ? datum.value : datum;
  if (raw === null || raw === undefined) return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
}
